import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, RotateCw, GripVertical } from "lucide-react";

// SuperAdmin — Landing-page marquee editor.
// Lets platform admins edit the "Trusted by..." company names that scroll
// on the public landing page. The list is capped at 24 entries (60 chars each)
// to match the backend validation.
export default function SuperAdminMarquee() {
  const [names, setNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/landing-marquee");
      setNames(r.data.names || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load marquee");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addRow = () => {
    if (names.length >= 24) {
      toast.error("Maximum 24 names allowed");
      return;
    }
    setNames([...names, ""]);
  };

  const updateRow = (idx, value) => {
    setNames(names.map((n, i) => (i === idx ? value : n)));
  };

  const removeRow = (idx) => {
    setNames(names.filter((_, i) => i !== idx));
  };

  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    const next = [...names];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setNames(next);
    setDragIdx(null);
  };

  const save = async () => {
    const clean = names.map((n) => n.trim()).filter(Boolean);
    if (clean.some((n) => n.length > 60)) {
      toast.error("Each name must be 60 characters or fewer");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/admin/landing-marquee", { names: clean });
      setNames(r.data.names || []);
      toast.success("Marquee saved — public landing page updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 grid place-items-center" data-testid="admin-marquee-loading">
        <Loader2 className="animate-spin" size={18} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-marquee-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-xl">Landing marquee — "Trusted by" strip</h2>
          <p className="text-xs text-[var(--ll-ink-soft)] mt-1 max-w-xl">
            Edit the scrolling company names on the public landing page. Drag rows to reorder.
            Max 24 entries, 60 characters each.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={saving}
            data-testid="admin-marquee-reload"
            className="px-3 py-2 border text-xs flex items-center gap-1.5 hover:bg-[var(--ll-bg-card)]"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <RotateCw size={12} /> Reload
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="admin-marquee-save"
            className="px-4 py-2 bg-[#0b5d9e] text-white text-xs flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Save marquee
          </button>
        </div>
      </div>

      <div className="border bg-white" style={{ borderColor: "var(--ll-border)" }}>
        <div className="flex items-center justify-between px-4 py-2 border-b" style={{ borderColor: "var(--ll-border)" }}>
          <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
            {names.length} / 24 names
          </div>
          <button
            onClick={addRow}
            disabled={names.length >= 24}
            data-testid="admin-marquee-add"
            className="text-xs px-2 py-1 border flex items-center gap-1 hover:bg-[var(--ll-bg-card)] disabled:opacity-50"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <Plus size={11} /> Add name
          </button>
        </div>

        {names.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="admin-marquee-empty">
            No names yet. Click <b>Add name</b> to start — or save empty to reset to defaults.
          </div>
        ) : (
          <ul className="divide-y" style={{ borderColor: "var(--ll-border)" }}>
            {names.map((n, idx) => (
              <li
                key={idx}
                draggable
                onDragStart={() => onDragStart(idx)}
                onDragOver={onDragOver}
                onDrop={() => onDrop(idx)}
                className={`flex items-center gap-2 px-3 py-2 ${dragIdx === idx ? "opacity-50 bg-[var(--ll-bg-card)]" : ""}`}
                data-testid={`admin-marquee-row-${idx}`}
              >
                <GripVertical size={14} className="text-[var(--ll-ink-muted)] cursor-grab" />
                <span className="text-[10px] font-mono-fin text-[var(--ll-ink-muted)] w-6 text-right">{idx + 1}</span>
                <input
                  type="text"
                  value={n}
                  onChange={(e) => updateRow(idx, e.target.value)}
                  maxLength={60}
                  placeholder="Company name"
                  data-testid={`admin-marquee-input-${idx}`}
                  className="flex-1 px-2 py-1 border text-sm bg-white"
                  style={{ borderColor: "var(--ll-border)" }}
                />
                <button
                  onClick={() => removeRow(idx)}
                  data-testid={`admin-marquee-remove-${idx}`}
                  aria-label="Remove name"
                  className="p-1.5 text-[var(--ll-ink-soft)] hover:text-red-600"
                >
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Live preview */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2">
          Preview
        </div>
        <div className="border overflow-hidden py-4" style={{ borderColor: "var(--ll-border)" }} data-testid="admin-marquee-preview">
          <div className="flex gap-10 px-6 overflow-x-auto">
            {(names.filter((n) => n.trim()).length ? names.filter((n) => n.trim()) : ["No names set"]).map((n, i) => (
              <span key={i} className="font-display font-bold text-base text-[var(--ll-ink-muted)] whitespace-nowrap">
                {n}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
